import { useEffect, useRef } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { darken, lighten, rgba } from 'polished';
import styled from 'styled-components';

import Img from '@/components/global/Img';
import { LinkWithQuery } from '@/components/global/LinkWithQuery';
import { gridColorSelector } from '@/components/v2/GridLines';
import { LayoutSubgrid } from '@/components/v2/Page/LayoutGrid';
import { styles as textStyles } from '@/components/v2/UtilityCss/Text';
import { type ContentfulAssetType } from '@/data/graphql-fragments/ContentfulAsset';
import { INTERNAL_LINKS } from '@/utils';

const Section = styled(LayoutSubgrid)`
  position: relative;
  background-color: ${({ theme }) => darken(0.0125, theme.palette.fg100)};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-image: ${({ theme }) => theme.textures.noise.img};
    background-size: ${({ theme }) => theme.textures.noise.size};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
    pointer-events: none;
  }

  &::before {
    --cutout-width: 264px;
    --cutout-left-x: calc(50% - var(--cutout-width) * 0.5 - 1px);
    --cutout-right-x: calc(50% + var(--cutout-width) * 0.5 + 1px);

    content: '';
    position: absolute;
    box-sizing: content-box;
    inset: -1px 0 -1px 0;
    border-top: 1px solid ${({ theme }) => gridColorSelector(theme, 'major')};
    border-bottom: 1px solid ${({ theme }) => gridColorSelector(theme, 'major')};

    clip-path: polygon(
      0% 0%,
      var(--cutout-left-x) 0%,
      var(--cutout-left-x) 5px,
      var(--cutout-right-x) 5px,
      var(--cutout-right-x) 0%,
      100% 0%,
      100% 100%,
      0% 100%
    );
  }
`;

const SectionLabel = styled.h2`
  position: absolute;
  left: 50%;
  width: min-content;
  white-space: nowrap;
  padding: 0 12px;
  color: ${({ theme }) => theme.palette.fg1200};
  text-transform: uppercase;
  line-height: 0.8;
  font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
  border-right: 1px solid ${({ theme }) => theme.palette.fg1200};
  border-left: 1px solid ${({ theme }) => theme.palette.fg1200};
  transform: translate(-50%, -50%);

  &::before,
  &::after {
    content: '';
    position: absolute;
    height: 1px;
    width: 7px;
    background-color: ${({ theme }) => theme.palette.fg1200};
    top: 50%;
    left: 0;
    transform: translate(-100%, -100%);
  }
  &::after {
    left: auto;
    right: 0;
    transform: translate(100%, -100%);
  }
`;

const Logos = styled.ul`
  display: flex;
  gap: 24px 64px;
  padding: 44px 32px 40px 32px;
  margin: 0 auto;
  flex-wrap: wrap;
  justify-content: center;
`;

interface Customer {
  customer_story?: {
    author: string;
    quote: {
      quote: string;
    };
  }[];
  id: string;
  logo: ContentfulAssetType;
  name: string;
  slug: string;
}

interface CustomerBannerQuery {
  contentfulCustomerBanner: {
    customerLogos: Customer[];
    id: string;
  };
}

export const CustomerStoryPopover = styled.div`
  --in-out-base: cubic-bezier(0.25, 0.1, 0.25, 1);

  position: absolute;
  width: 300px;
  bottom: calc(100% + 16px);
  left: 0;
  transform: translateY(16px);
  opacity: 0;
  padding: 8px 16px 16px 16px;
  z-index: 2;
  transition:
    transform 0.14s var(--in-out-base),
    opacity 0.14s var(--in-out-base);
  pointer-events: none;
  display: flex;
  flex-direction: column;

  background-image: ${({ theme }) => {
    const stop1 = rgba(theme.palette.fg100, 0.98);
    const stop2 = rgba(darken(0.03125, theme.palette.fg100), 1);
    return `radial-gradient(at 0% 0%, ${stop1} 48%, ${stop2})`;
  }};
  box-shadow: ${({ theme }) => {
    const lowlight = `0px 0.5px 0 0.75px ${lighten(0.125, theme.palette.fg100)}`;
    const shadow = '4px 8px 12px -6px hsla(0 0% 0% / 0.64)';
    return [lowlight, shadow].join(',');
  }};
  border-radius: 4px;

  &[data-align='right'] {
    left: auto;
    right: 0;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    display: none;
  }
`;

const LogoWrapperLink = styled(LinkWithQuery)`
  display: block;
  position: relative;
  height: 40px;
  border-bottom: none;

  &:hover {
    background: transparent;
  }

  @media (hover: hover) {
    &:hover ${CustomerStoryPopover} {
      opacity: 1;
      transform: none;
    }
  }

  &[data-customer-story='true'] {
    &::after {
      position: absolute;
      content: '';
      height: 1px;
      inset: auto 0 -5px 0;
    }
  }

  img {
    height: 100%;
  }
`;

const LogoWrapper = styled.div`
  position: relative;
  height: 40px;
  border-bottom: none;

  &[data-customer-story='true'] {
    &::after {
      position: absolute;
      content: '';
      height: 1px;
      inset: auto 0 -5px 0;
    }
  }

  img {
    height: 100%;
  }
`;

const CustomerStoryIndicator = styled.div`
  position: absolute;
  top: -8px;
  right: -12px;
  height: 12px;
  aspect-ratio: 1 / 1;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background-color: ${({ theme }) =>
    rgba(darken(0.08, theme.palette.fgLoud), 0.16)};
  border: 1px solid
    ${({ theme }) => rgba(darken(0.08, theme.palette.fgLoud), 0.16)};
  white-space: nowrap;
  color: ${({ theme }) => rgba(darken(0.08, theme.palette.fgLoud), 0.8)};
  font-size: 8px;

  &::before,
  &::after {
    content: '';
    height: 4px;
    width: 1px;
    background-color: currentColor;
    transform-origin: center center;
    grid-area: 1 / -1;
  }

  &::after {
    height: 1px;
    width: 4px;
  }
`;

const PopoverQuote = styled.q`
  font-weight: ${({ theme }) => theme.fontWeight.LIGHT};
`;

const PopoverLogo = styled(Img)`
  height: 32px;
  display: block;
  margin: 4px auto 8px auto;
`;

function QuoteIcon() {
  return (
    <svg
      fill="none"
      height="12"
      viewBox="0 0 15 15"
      width="12"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6.91699 1.30798V6.30603L6.85352 6.41931L3.10938 13.139L2.85547 13.5951L2.41113 13.3217L1.16309 12.5541L0.770508 12.3119L0.979492 11.9008L3.45898 7.02576L1.7793 6.40466L1.45312 6.28455V1.30798H6.91699ZM13.8291 1.30798V6.30603L13.7656 6.41931L10.0215 13.139L9.76758 13.5951L9.32324 13.3217L8.0752 12.5541L7.68164 12.3119L7.8916 11.9008L10.3711 7.02576L8.69141 6.40466L8.36523 6.28455V1.30798H13.8291Z"
        stroke="currentColor"
      />
    </svg>
  );
}

const PopoverDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;

  &::before,
  &::after {
    content: '';
    display: block;
    flex-grow: 1;
    height: 1px;
    background-color: ${({ theme }) => theme.palette.fgA600};
  }
`;

const Author = styled.span`
  font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
  margin-top: 12px;
  color: ${({ theme }) => theme.palette.fg1100};
`;

function Logo({ customer }: { customer: Customer }) {
  const customerStory = customer.customer_story?.[0];

  return customerStory ? (
    <LogoWrapperLink to={`${INTERNAL_LINKS.Customers}${customer.slug}`}>
      <CustomerStoryIndicator />
      <Img
        alt={`Hex customer: ${customer.name}`}
        asset={customer.logo}
        height={40}
        style={{
          aspectRatio: `${customer.logo.width} / ${customer.logo.height}`
        }}
        width="auto"
      />
      <CustomerStoryPopover data-cutomer={customer.name}>
        <PopoverLogo
          alt={`Hex customer: ${customer.name}`}
          asset={customer.logo}
        />
        <PopoverDivider>
          <QuoteIcon />
        </PopoverDivider>
        <PopoverQuote className={textStyles.base}>
          "{customerStory.quote.quote}"
        </PopoverQuote>
        <Author className={textStyles.annotation}>
          {customerStory.author}
        </Author>
      </CustomerStoryPopover>
    </LogoWrapperLink>
  ) : (
    <LogoWrapper>
      <Img
        alt={`Hex customer: ${customer.name}`}
        asset={customer.logo}
        height={40}
        style={{
          aspectRatio: `${customer.logo.width} / ${customer.logo.height}`
        }}
        width="auto"
      />
    </LogoWrapper>
  );
}

const Li = styled.li`
  display: block;
  position: relative;
`;

const LAccents = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  grid-column: 1 / -1;
  pointer-events: none;
  z-index: 1;

  &::before,
  &::after {
    content: '';
    height: 6px;
    width: 6px;
    position: absolute;
    border-bottom: 1px solid ${({ theme }) => theme.palette.fg1200};
    border-left: 1px solid ${({ theme }) => theme.palette.fg1200};
    left: -1px;
    top: -7px;
  }

  &::after {
    border-left: none;
    border-right: 1px solid ${({ theme }) => theme.palette.fg1200};
    left: auto;
    right: -1px;
  }
`;

export function CustomerBanner() {
  const logosRef = useRef<HTMLUListElement>(null);
  const data: CustomerBannerQuery = useStaticQuery(graphql`
    query {
      contentfulCustomerBanner(
        id: { eq: "76677737-296f-50aa-95a0-79e5cf5c817c" }
      ) {
        id
        title
        customerLogos {
          id
          name
          logo {
            ...ContentfulAsset
          }
          slug
          customer_story {
            id
            quote {
              quote
            }
            description {
              description
            }
            author
          }
        }
      }
    }
  `);

  useEffect(() => {
    const logoListNode = logosRef.current;
    if (!logoListNode) return;

    function alignPopovers() {
      const logoListNode = logosRef.current;
      if (!logoListNode) return;

      const popoverNodes = logoListNode.querySelectorAll(CustomerStoryPopover);
      if (!popoverNodes) return;

      const windowWidth = window.innerWidth - 100;
      for (const node of popoverNodes) {
        const bb = node.getBoundingClientRect();
        const condition = bb.left + bb.width > windowWidth;
        const alignment = condition ? 'right' : 'left';
        node.setAttribute('data-align', alignment);
      }
    }

    const observer = new ResizeObserver(() => {
      alignPopovers();
    });
    alignPopovers();

    observer.observe(logoListNode);
    return () => {
      observer.unobserve(logoListNode);
    };
  }, []);

  return (
    <Section data-content-grid>
      <LAccents />
      <SectionLabel className={textStyles.annotation}>
        Trusted by leading data companies
      </SectionLabel>
      <Logos ref={logosRef}>
        {data.contentfulCustomerBanner.customerLogos
          .filter(customer => customer.logo !== null)
          .map(customer => (
            <Li key={customer.id}>
              <Logo customer={customer} />
            </Li>
          ))}
      </Logos>
    </Section>
  );
}
